import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

interface CopyableTrackingProps {
  trackingNumber: string;
  carrier?: string | null;
  /** Stop the click from bubbling to a clickable row behind it. */
  stopPropagation?: boolean;
  className?: string;
}

/**
 * BLU-660 — a shipped order's tracking number rendered as a copy-to-clipboard
 * control (there is no first-party carrier tracking-URL map in the app, so the
 * value is copyable rather than linked). Micro-interaction: the copy glyph
 * swaps to a check for 1.5s and an `aria-live` region announces "Copied" for
 * screen-reader users. Used in both the list row and the detail sheet.
 */
export function CopyableTracking({
  trackingNumber,
  carrier,
  stopPropagation = false,
  className,
}: CopyableTrackingProps) {
  const { t } = useTranslation("clientOrders");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) event.stopPropagation();
      if (!navigator.clipboard) return;
      void navigator.clipboard
        .writeText(trackingNumber)
        .then(() => setCopied(true))
        .catch(() => {
          /* clipboard blocked — no-op, the value stays visible to copy manually */
        });
    },
    [trackingNumber, stopPropagation],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums",
        "transition-colors duration-150 motion-reduce:transition-none hover:bg-muted/60",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        className,
      )}
      aria-label={
        copied
          ? t("tracking.copied")
          : carrier
            ? t("tracking.copyAriaWithCarrier", {
                number: trackingNumber,
                carrier,
              })
            : t("tracking.copyAria", { number: trackingNumber })
      }
    >
      <span className="truncate">{trackingNumber}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
      ) : (
        <Copy
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground"
          aria-hidden="true"
        />
      )}
      <span aria-live="polite" className="sr-only">
        {copied ? t("tracking.copiedAnnounce") : ""}
      </span>
    </button>
  );
}
